/**
 * AdminAssistant — Ramos's view of Ask BLIP: the change-request queue, every
 * conversation (read-only transcripts — where the reports confuse people),
 * and spend per person per day.
 */
import { useCallback, useEffect, useState } from 'react';
import type {
  AssistantConversation,
  AssistantRequest,
  AssistantRequestStatus,
  AssistantUsageRow,
} from '@aurora/shared';
import { api, ApiClientError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { AssistantThread } from '../components/AssistantThread';

type Tab = 'requests' | 'conversations' | 'usage';
const STATUSES: AssistantRequestStatus[] = [
  'open',
  'accepted',
  'done',
  'declined',
];

function Requests(): JSX.Element {
  const [items, setItems] = useState<AssistantRequest[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const load = useCallback(() => {
    api.assistant
      .requests()
      .then((r) => setItems(r.items))
      .catch((e: unknown) =>
        setError(
          e instanceof ApiClientError ? e.message : 'Could not load requests',
        ),
      );
  }, []);
  useEffect(load, [load]);

  const update = async (
    id: string,
    status: AssistantRequestStatus,
  ): Promise<void> => {
    try {
      await api.assistant.updateRequest(id, status, notes[id]);
      load();
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Update failed');
    }
  };

  if (error) return <div className="form-error">{error}</div>;
  if (items.length === 0)
    return <p className="muted">No change requests yet.</p>;
  return (
    <div className="ab-requests">
      {items.map((r) => (
        <article key={r.id} className={`ab-req ab-req-${r.status}`}>
          <header>
            <span className="pill">{r.kind}</span> <strong>{r.title}</strong>
            <span className="muted small">
              {' '}
              · {r.userName ?? 'unknown'} ·{' '}
              {new Date(r.createdAt).toLocaleString()}
            </span>
          </header>
          <p className="ab-req-detail">{r.detail}</p>
          {Object.keys(r.related).length > 0 && (
            <p className="muted small">
              {Object.entries(r.related)
                .map(([k, v]) => `${k}: ${String(v)}`)
                .join(' · ')}
            </p>
          )}
          <p className="muted small">
            {r.notifiedAt
              ? `Emailed to Ramos ${new Date(r.notifiedAt).toLocaleString()}`
              : `Not emailed${r.notifyError ? ` — ${r.notifyError}` : ''}`}
          </p>
          <div className="ab-req-actions">
            <select
              value={r.status}
              onChange={(e) =>
                void update(r.id, e.target.value as AssistantRequestStatus)
              }
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <input
              type="text"
              placeholder={
                r.resolutionNote ??
                'Resolution note (saved with the next status change)'
              }
              value={notes[r.id] ?? ''}
              onChange={(e) =>
                setNotes((n) => ({ ...n, [r.id]: e.target.value }))
              }
            />
          </div>
        </article>
      ))}
    </div>
  );
}

function Conversations(): JSX.Element {
  const [items, setItems] = useState<AssistantConversation[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api.assistant
      .list(true)
      .then((r) => setItems(r.items))
      .catch((e: unknown) =>
        setError(
          e instanceof ApiClientError
            ? e.message
            : 'Could not load conversations',
        ),
      );
  }, []);
  if (error) return <div className="form-error">{error}</div>;
  return (
    <div className="ab-page-grid">
      <nav className="ab-history card">
        {items.length === 0 && (
          <p className="muted small">No conversations yet.</p>
        )}
        <ul>
          {items.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                className={
                  c.id === open ? 'ab-hist-item active' : 'ab-hist-item'
                }
                onClick={() => setOpen(c.id)}
              >
                <span className="ab-hist-title">{c.title}</span>
                <span className="muted small">
                  {c.userName} · {new Date(c.updatedAt).toLocaleDateString()} ·
                  ${(c.cost ?? 0).toFixed(2)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </nav>
      <section className="ab-page-thread card">
        {open ? (
          <AssistantThread key={open} conversationId={open} readOnly />
        ) : (
          <p className="muted">Pick a conversation to read it.</p>
        )}
      </section>
    </div>
  );
}

function Usage(): JSX.Element {
  const [items, setItems] = useState<AssistantUsageRow[]>([]);
  useEffect(() => {
    api.assistant
      .usage()
      .then((r) => setItems(r.items))
      .catch(() => setItems([]));
  }, []);
  const total = items.reduce((s, r) => s + r.cost, 0);
  return (
    <div>
      <p className="muted">
        Last 30 days: ${total.toFixed(2)} across{' '}
        {items.reduce((s, r) => s + r.questions, 0)} questions.
      </p>
      <table className="table">
        <thead>
          <tr>
            <th>Day (UTC)</th>
            <th>Person</th>
            <th>Questions</th>
            <th>Cost</th>
          </tr>
        </thead>
        <tbody>
          {items.map((r) => (
            <tr key={`${r.day}-${r.userId}`}>
              <td>{r.day}</td>
              <td>{r.userName}</td>
              <td>{r.questions}</td>
              <td>${r.cost.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AdminAssistant(): JSX.Element {
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>('requests');
  if (user?.role !== 'admin')
    return <div className="form-error">Admins only.</div>;
  return (
    <div>
      <div className="page-head">
        <div>
          <h1>Ask BLIP — admin</h1>
          <p className="muted">
            Change requests filed through the assistant, every conversation, and
            AI spend.
          </p>
        </div>
      </div>
      <div className="ab-tabs">
        {(['requests', 'conversations', 'usage'] as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            className={t === tab ? 'btn btn-primary' : 'btn btn-ghost'}
            onClick={() => setTab(t)}
          >
            {t === 'requests'
              ? 'Change requests'
              : t === 'conversations'
                ? 'Conversations'
                : 'Usage'}
          </button>
        ))}
      </div>
      {tab === 'requests' && <Requests />}
      {tab === 'conversations' && <Conversations />}
      {tab === 'usage' && <Usage />}
    </div>
  );
}
